/**
 * Paint toolbar: floats over the top of the map while a plan is open.
 *
 * Tools (brush / eraser / pan), brush size, "protect other districts",
 * undo/redo, and the save-status chip all sit next to the canvas they act
 * on — no trip to a sidebar mid-stroke, and they survive full-map mode.
 *
 * Labels: Brush / Erase / Pan and Protect show their names when the map is
 * wide enough (measured on the map's toolbar dock, since the side panels
 * change the map's width independently of the window); when it's narrow,
 * only the selected tool keeps its name. Every control has a tooltip on
 * hover and on keyboard focus, with its shortcut.
 *
 * Save contract: strokes persist via a debounced bulk upsert (guest drafts
 * persist to this browser); the chip offers an explicit Save and surfaces a
 * retry on failure — never silent loss.
 */
import { useEffect, useState } from 'react';
import { Tooltip } from '../Tooltip';
import {
  BRUSH_LABELS,
  BRUSH_RADIUS_PX,
  usePaintingStore,
  type BrushSize,
  type PaintMode,
} from '../../stores/painting';
import {
  AlertIcon,
  BrushIcon,
  CloudCheckIcon,
  EraserIcon,
  HandIcon,
  KeyboardIcon,
  LockIcon,
  RedoIcon,
  UndoIcon,
} from '../icons';

const TOOLS: ReadonlyArray<{ mode: PaintMode; label: string; key: string; Icon: typeof BrushIcon; hint: string }> = [
  { mode: 'paint', label: 'Brush', key: 'B', Icon: BrushIcon, hint: 'Brush: paint blocks into the selected district' },
  { mode: 'erase', label: 'Erase', key: 'E', Icon: EraserIcon, hint: 'Erase: make blocks unassigned again' },
  { mode: 'pan', label: 'Pan', key: 'V', Icon: HandIcon, hint: 'Pan: drag to move the map without painting' },
];

/** Map dock width (px) at which every tool keeps its text label. */
const ROOMY_DOCK_PX = 760;

/**
 * True when the map's toolbar dock is wide enough for full labels. Takes
 * the toolbar element itself (set via a callback ref) so it starts
 * measuring as soon as the toolbar appears, i.e. when a plan opens.
 */
function useRoomyDock(node: HTMLElement | null): boolean {
  const [roomy, setRoomy] = useState(true);
  useEffect(() => {
    const dock = node?.closest<HTMLElement>('[data-toolbar-dock]') ?? node?.parentElement;
    if (!dock || typeof ResizeObserver === 'undefined') return;
    const update = (): void => setRoomy(dock.clientWidth >= ROOMY_DOCK_PX);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(dock);
    return () => observer.disconnect();
  }, [node]);
  return roomy;
}

function SaveChip({ compact }: { compact: boolean }): JSX.Element | null {
  const planId = usePaintingStore((s) => s.planId);
  const dirtyCount = usePaintingStore((s) => s.dirtyGeoids.length);
  const saveState = usePaintingStore((s) => s.saveState);
  const saveError = usePaintingStore((s) => s.saveError);
  const lastSavedAt = usePaintingStore((s) => s.lastSavedAt);
  const isLocalDraft = usePaintingStore((s) => s.isLocalDraft);
  const saveNow = usePaintingStore((s) => s.saveNow);
  const retrySave = usePaintingStore((s) => s.retrySave);

  if (!planId) return null;

  if (saveState === 'error') {
    return (
      <div className="flex items-center gap-1.5" role="alert">
        <Tooltip label={saveError ?? 'Save failed'}>
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-200">
            <AlertIcon size={14} /> Not saved
          </span>
        </Tooltip>
        <button type="button" onClick={() => void retrySave()} className="btn-secondary btn-sm">
          Retry
        </button>
      </div>
    );
  }
  if (saveState === 'saving') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600" aria-live="polite">
        <span className="h-2 w-2 animate-pulse rounded-full bg-stone-400" aria-hidden="true" />
        Saving…
      </span>
    );
  }
  if (dirtyCount > 0) {
    return (
      <Tooltip label="Changes save automatically in a moment. Click to save now.">
        <button
          type="button"
          onClick={() => void saveNow()}
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
          aria-live="polite"
        >
          <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          {dirtyCount.toLocaleString('en-US')} unsaved
        </button>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      label={
        isLocalDraft
          ? 'Saved in this browser only. Save to your account to keep it safe.'
          : lastSavedAt
            ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}`
            : 'Everything is saved'
      }
    >
      <span
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium text-emerald-700"
        aria-live="polite"
      >
        <CloudCheckIcon size={16} />
        {compact
          ? isLocalDraft && !lastSavedAt
            ? 'Draft'
            : 'Saved'
          : isLocalDraft
            ? lastSavedAt
              ? 'Saved in this browser'
              : 'Guest draft'
            : lastSavedAt
              ? 'All changes saved'
              : 'Up to date'}
      </span>
    </Tooltip>
  );
}

function Shortcuts(): JSX.Element {
  const rows: Array<[string, string]> = [
    ['B / E / V', 'Brush · Erase · Pan'],
    ['1 – 9', 'Pick a district'],
    ['[  ]', 'Smaller / larger brush'],
    ['L', 'Protect other districts'],
    ['⌘Z / Ctrl+Z', 'Undo (add Shift to redo)'],
    ['Esc', 'Cancel the current stroke'],
    ['Right-drag', 'Pan while painting'],
  ];
  return (
    <div className="floating absolute bottom-full right-0 z-30 mb-2 w-64 p-3 text-xs" role="dialog" aria-label="Keyboard shortcuts">
      <p className="panel-title mb-2">Shortcuts</p>
      <dl className="space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <dt className="font-mono text-[11px] font-semibold text-stone-700">{k}</dt>
            <dd className="text-right text-stone-500">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function PaintToolbar(): JSX.Element | null {
  const planId = usePaintingStore((s) => s.planId);
  const canUndo = usePaintingStore((s) => s.undoStack.length > 0);
  const canRedo = usePaintingStore((s) => s.redoStack.length > 0);
  const undo = usePaintingStore((s) => s.undo);
  const redo = usePaintingStore((s) => s.redo);
  const paintMode = usePaintingStore((s) => s.paintMode);
  const setPaintMode = usePaintingStore((s) => s.setPaintMode);
  const brushSize = usePaintingStore((s) => s.brushSize);
  const setBrushSize = usePaintingStore((s) => s.setBrushSize);
  const lockAssigned = usePaintingStore((s) => s.lockAssigned);
  const setLockAssigned = usePaintingStore((s) => s.setLockAssigned);
  const [showKeys, setShowKeys] = useState(false);
  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null);
  const roomy = useRoomyDock(rootNode);

  if (!planId) return null;

  const iconBtn =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-stone-600 transition-colors hover:bg-stone-100 hover:text-ink disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent';
  const brushDisabled = paintMode === 'pan';

  return (
    <div ref={setRootNode} className="relative max-w-full">
    <div className="floating flex max-w-full flex-wrap items-center justify-center gap-0.5 p-1" role="toolbar" aria-label="Drawing tools">
      <div className="flex items-center gap-0.5 rounded-lg bg-stone-100 p-0.5" role="group" aria-label="Tool">
        {TOOLS.map(({ mode, label, key, Icon, hint }) => {
          const active = paintMode === mode;
          return (
            <Tooltip key={mode} label={hint} shortcut={key}>
              <button
                type="button"
                onClick={() => setPaintMode(mode)}
                aria-pressed={active}
                aria-label={label}
                className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold transition-colors ${
                  active ? 'bg-white text-ink shadow-sm ring-1 ring-stone-200' : 'text-stone-500 hover:text-ink'
                }`}
              >
                <Icon size={16} />
                {(roomy || active) && <span>{label}</span>}
              </button>
            </Tooltip>
          );
        })}
      </div>

      <span className="mx-1 h-6 w-px shrink-0 bg-stone-200" aria-hidden="true" />

      <div
        className={`flex items-center gap-0.5 ${brushDisabled ? 'opacity-40' : ''}`}
        role="radiogroup"
        aria-label="Brush size"
      >
        {([0, 1, 2, 3] as BrushSize[]).map((size) => {
          const active = brushSize === size;
          const dot = size === 0 ? 5 : Math.round(6 + BRUSH_RADIUS_PX[size] / 5);
          return (
            <Tooltip
              key={size}
              label={
                brushDisabled
                  ? 'Brush size (switch to Brush or Erase to change it)'
                  : `Brush size: ${BRUSH_LABELS[size].toLowerCase()}${size === 0 ? '' : ' area'}`
              }
              shortcut="[ ]"
            >
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`Brush size: ${BRUSH_LABELS[size]}`}
              disabled={brushDisabled}
              onClick={() => setBrushSize(size)}
              className={`inline-flex h-9 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                active ? 'bg-tenant-soft ring-1 ring-tenant-primary/40' : 'hover:bg-stone-100'
              }`}
            >
              {size === 0 ? (
                <span
                  aria-hidden="true"
                  className={`block h-[9px] w-[9px] rounded-[2px] ${active ? 'bg-tenant-primary' : 'bg-stone-400'}`}
                />
              ) : (
                <span
                  aria-hidden="true"
                  className={`block rounded-full ${active ? 'bg-tenant-primary' : 'bg-stone-400'}`}
                  style={{ width: dot, height: dot }}
                />
              )}
            </button>
            </Tooltip>
          );
        })}
      </div>

      <Tooltip
        label={
          lockAssigned
            ? 'On: the brush only fills unassigned blocks, so districts you’ve already drawn stay as they are. Click to turn off.'
            : 'Protect other districts: when on, the brush only fills unassigned blocks and never paints over a district you’ve already drawn.'
        }
        shortcut="L"
      >
        <button
          type="button"
          onClick={() => setLockAssigned(!lockAssigned)}
          aria-pressed={lockAssigned}
          aria-label="Protect other districts"
          disabled={brushDisabled}
          className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors disabled:opacity-40 ${
            lockAssigned
              ? 'bg-tenant-soft text-tenant-primary ring-1 ring-tenant-primary/40'
              : 'text-stone-500 hover:bg-stone-100 hover:text-ink'
          }`}
        >
          <LockIcon size={16} open={!lockAssigned} />
          {(roomy || lockAssigned) && <span>{lockAssigned ? 'Protected' : 'Protect'}</span>}
        </button>
      </Tooltip>

      <span className="mx-1 h-6 w-px shrink-0 bg-stone-200" aria-hidden="true" />

      <Tooltip label={canUndo ? 'Undo' : 'Nothing to undo'} shortcut="⌘/Ctrl Z">
        <button type="button" onClick={undo} disabled={!canUndo} className={iconBtn} aria-label="Undo">
          <UndoIcon />
        </button>
      </Tooltip>
      <Tooltip label={canRedo ? 'Redo' : 'Nothing to redo'} shortcut="⇧⌘/Ctrl Shift Z">
        <button type="button" onClick={redo} disabled={!canRedo} className={iconBtn} aria-label="Redo">
          <RedoIcon />
        </button>
      </Tooltip>

      <span className="mx-1 h-6 w-px shrink-0 bg-stone-200" aria-hidden="true" />

      <SaveChip compact={!roomy} />

      <div className="hidden md:block">
        <Tooltip label="Keyboard shortcuts">
          <button
            type="button"
            onClick={() => setShowKeys((v) => !v)}
            aria-expanded={showKeys}
            className={iconBtn}
            aria-label="Keyboard shortcuts"
          >
            <KeyboardIcon />
          </button>
        </Tooltip>
      </div>
    </div>
    {showKeys && <Shortcuts />}
    </div>
  );
}
